import {
  type RefObject,
  type SyntheticEvent,
  useEffect,
  useState,
} from "react";

type UseVideoPlayer = {
  isPlaying: boolean;
  isMuted: boolean;
  currentTime: number;
  duration: number;
  togglePlayback: () => void;
  toggleSound: () => void;
  seekToRatio: (ratio: number) => void;
  videoEventHandlers: {
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
    onVolumeChange: (event: SyntheticEvent<HTMLVideoElement>) => void;
    onLoadedMetadata: (event: SyntheticEvent<HTMLVideoElement>) => void;
    onDurationChange: (event: SyntheticEvent<HTMLVideoElement>) => void;
    onTimeUpdate: (event: SyntheticEvent<HTMLVideoElement>) => void;
  };
};

export const useVideoPlayer = (
  videoRef: RefObject<HTMLVideoElement | null>,
  videoSrc: string | null,
): UseVideoPlayer => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;

    void video.play().catch(() => setIsPlaying(false));
  }, [videoSrc]);

  const syncTimeline = (video: HTMLVideoElement) => {
    setCurrentTime(video.currentTime);
    setDuration(Number.isFinite(video.duration) ? video.duration : 0);
  };

  const togglePlayback = () => {
    const video = videoRef.current;
    if (video === null) return;

    if (video.paused) {
      void video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
    }
  };

  const toggleSound = () => {
    const video = videoRef.current;
    if (video === null) return;

    video.muted = !video.muted;
  };

  const seekToRatio = (ratio: number) => {
    const video = videoRef.current;
    if (video === null || duration === 0) return;

    video.currentTime = ratio * duration;
    setCurrentTime(video.currentTime);
  };

  return {
    isPlaying,
    isMuted,
    currentTime,
    duration,
    togglePlayback,
    toggleSound,
    seekToRatio,
    videoEventHandlers: {
      onPlay: () => setIsPlaying(true),
      onPause: () => setIsPlaying(false),
      onEnded: () => setIsPlaying(false),
      onVolumeChange: (event) => setIsMuted(event.currentTarget.muted),
      onLoadedMetadata: (event) => syncTimeline(event.currentTarget),
      onDurationChange: (event) => syncTimeline(event.currentTarget),
      onTimeUpdate: (event) => syncTimeline(event.currentTarget),
    },
  };
};
